import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
    CardInfoService,
    CardInfoFacade,
    ShoppingCartFacade,
    ApplicationFacade,
    NotificationFacade
} from '@ridango/components';
import { BaseComponent, PersonalCodeValidator, Ticket } from '@ridango/core';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'rwidget-travelcard-tickets',
    templateUrl: './travelcard-tickets.component.html'
})
export class TravelcardTicketsComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() required = false;
    cardNumberForm: FormGroup;
    showCardTypeSelect: boolean;
    cardBalance;
    showTab = 'activeTickets';
    locale = 'en';

    termsModalVisible = false;
    termsAgreed = false;
    termsUrl: string;
    terms = null;

    travelcardLength = this.env.PURCHASE_FLOW?.UTN_TRAVEL_CARD_LENGTH ?? 11;

    cardNumber$ = this.cardInfoFacade.cardNumber$;
    cardBalance$ = this.cardInfoFacade.cardBalance$;
    cardTickets$ = this.cardInfoFacade.cardTickets$;
    termsAgreed$: Observable<boolean> = this.cartFacade.termsAgreed$;
    termsUrl$: Observable<string> = this.applicationFacade.termsUrl$;

    constructor(
        private cardInfoService: CardInfoService,
        private cardInfoFacade: CardInfoFacade,
        private translate: TranslateService,
        private cartFacade: ShoppingCartFacade,
        private applicationFacade: ApplicationFacade,
        private route: ActivatedRoute,
        private personalCodeValidator: PersonalCodeValidator,
        private notificationFacade: NotificationFacade,
        @Inject('ENV') public env: Env
    ) {
        super();
    }

    ngOnInit() {
        const patternMinLength = this.env.PURCHASE_FLOW?.UTN_TRAVEL_CARD_LENGTH ?? 11;
        const patternMaxLength = this.env.PURCHASE_FLOW?.ISIC_CARD_LENGTH ?? 14;
        this.locale = this.translate.currentLang;
        this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            this.locale = event.lang;
        });
        this.applicationFacade.termsUrl$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((url) => (this.termsUrl = url));

        this.termsAgreed$.pipe(takeUntil(this.componentDestroyed$)).subscribe((t) => {
            this.termsAgreed = t;
        });

        this.cardNumberForm = new FormGroup({
            card_number: new FormControl('', [Validators.pattern('[0-9]*'), Validators.required])
        });

        this.cardNumberForm.controls['card_number'].setValidators([
            Validators.pattern('^[0-9a-zA-Z]*$'),
            Validators.minLength(patternMinLength),
            Validators.maxLength(patternMaxLength),
            Validators.required,
            this.personalCodeValidator.getValidator(true)
        ]);

        this.route.queryParams.pipe(takeUntil(this.componentDestroyed$)).subscribe((params) => {
            const cardId = params['cardId'];
            if (cardId && cardId.length >= this.travelcardLength) {
                this.cardNumberForm.controls['card_number'].setValue(cardId);
            }
        });
    }

    submitCardNumber() {
        this.cardNumberForm.controls['card_number'].markAsTouched();

        if (this.cardNumberForm.valid) {
            const cardNumber = this.cardNumberForm.controls['card_number'].value.toLocaleUpperCase();
            this.cardInfoFacade.setCardNumber(cardNumber);
            this.notificationFacade.removeAll();

            this.cardInfoService
                .fetchTickets(cardNumber)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(
                    (t: Ticket[]) => {
                        t.map((ticket) => {
                            ticket.card_id = cardNumber;
                        });
                        this.cardInfoFacade.setCardTickets(t);
                    },
                    (error) => this.handleUnknownCardError(error)
                );

            this.cardInfoService
                .fetchBalance(cardNumber)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(
                    (b) => {
                        this.cardInfoFacade.setCardBalance(b);
                    },
                    (error) => this.handleUnknownCardError(error)
                );
        } else {
            this.cardInfoFacade.setCardNumber(null);
            this.cardInfoFacade.setCardTickets(null);
            this.cardInfoFacade.setCardBalance(null);
        }
        this.notificationFacade.removeAll();
    }

    private handleUnknownCardError(error: any): void {
        if (error.error === 'UNKNOWN_CARD') {
            this.notificationFacade.error(this.translate.instant('UNKNOWN_CARD'));
        }
    }

    switchTab(tab) {
        this.showTab = tab;
    }

    toggleTermsAgreed() {
        this.cartFacade.setCartAgreement(!this.termsAgreed);
    }

    agreeTerms(agr: boolean) {
        this.termsModalVisible = false;
        this.cartFacade.setCartAgreement(agr);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
